import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import {
  Check,
  ChevronDown,
  ChevronUp,
  Droplets,
  FlaskConical,
  Pencil,
  Plus,
  Save,
  Trash2
} from "lucide-react";
import { useGetCipSipChemicalTypes } from "../../../../../api/static-data";
import { Button } from "../../../../../components/ui/common/button";
import { Card, CardContent, CardHeader } from "../../../../../components/ui/common/card";
import { Form } from "../../../../../components/ui/common/form";
import { cn } from "../../../../../lib/utils";
import { FormInput } from "../../../../../components/ui/Input/FormInput/FormInput";
import { FormInputNumber } from "../../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../../components/ui/Input/FormSelector/FormSelect";
import { PER_TYPES, PER_WEIGHT_UNITS } from "../../../../../config/optionsConstants";
import {
  CipSipChemical,
  CipSipFormValues,
  CipSipRecord
} from "../../../../../types/prodcut-data-review/product-data.types";

const INTERVAL_OPTIONS = PER_TYPES.map(({ value, text }) => ({ value, label: text }));
const PROD_UNIT_OPTIONS = PER_WEIGHT_UNITS.map(({ value, text }) => ({ value, label: text }));

const DEFAULT_CIP_FORM_VALUES: CipSipFormValues = {
  energyConsumption: "",
  manufacturer: "",
  modelNumber: "",
  steamFlow: "",
  steamPressure: "",
  wasteWater: "",
  waterConsumption: "",
  frequency: { amount: "", per: "week" },
  productionAmount: { amount: "", per: "week", perUnit: "kg" },
  chemical: []
};

const recordToFormValues = (rec: CipSipRecord): CipSipFormValues => {
  const { id, ...rest } = rec;
  return { ...rest, chemical: rest.chemical.map(ch => ({ ...ch })) };
};

type CipSipSectionFormValues = CipSipFormValues & {
  chemicalType: string;
  concentration: string;
  consumption: string;
};

interface Props {
  records: CipSipRecord[];
  disabled: boolean;
  gateKind: "farm" | "process";
  onAdd: (values: CipSipFormValues) => void;
  onUpdate: (id: string, patch: CipSipFormValues) => void;
  onRemove: (id: string) => void;
}

const CipSipSection = ({ records, disabled, gateKind, onAdd, onUpdate, onRemove }: Props) => {
  const existing = records[0] ?? null;

  const [open, setOpen] = useState(false);
  const [chemicalData, setChemicalData] = useState<CipSipChemical[]>(existing?.chemical ?? []);
  const [enableEdit, setEnableEdit] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [enableUpdateButton, setEnableUpdateButton] = useState(false);

  const { data: cipSipChemicalTypes } = useGetCipSipChemicalTypes();

  const formValues = useMemo(() => {
    setChemicalData(existing?.chemical ?? []);
    const base = existing ? recordToFormValues(existing) : DEFAULT_CIP_FORM_VALUES;
    return { ...base, chemicalType: "", concentration: "", consumption: "" };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id]);

  const methods = useForm<CipSipSectionFormValues>({
    defaultValues: formValues,
    values: formValues
  });
  const { control, handleSubmit, getValues, setValue, watch } = methods;

  const chemicalType = watch("chemicalType");

  const addNewChemical = () => {
    const chemicalTypeValue = getValues("chemicalType");
    const concentration = getValues("concentration");
    const consumption = getValues("consumption");

    if (!chemicalTypeValue) return;

    const chemicalName =
      cipSipChemicalTypes?.find((item: any) => String(item.value) === String(chemicalTypeValue))
        ?.text ?? chemicalTypeValue;

    const newChemical: CipSipChemical = { chemicalName, concentration, consumption };

    if (enableEdit && editIndex !== null) {
      setChemicalData(prev =>
        prev.map((item, i) => (i === editIndex ? { ...item, ...newChemical } : item))
      );
      setEnableEdit(false);
      setEditIndex(null);
    } else {
      setChemicalData(prev => [...prev, newChemical]);
    }

    setValue("concentration", "");
    setValue("consumption", "");
    setValue("chemicalType", "");
    setEnableUpdateButton(false);
  };

  const onEditChemical = (item: CipSipChemical, index: number) => {
    setEnableEdit(true);
    setEditIndex(index);
    setEnableUpdateButton(true);
    setValue("concentration", item.concentration);
    setValue("consumption", item.consumption);
    const chemicalValue = cipSipChemicalTypes?.find(
      (data: any) => data.text === item.chemicalName
    )?.value;
    setValue("chemicalType", chemicalValue ? String(chemicalValue) : "");
  };

  const removeChemical = (index: number) => {
    setChemicalData(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = (values: CipSipSectionFormValues) => {
    const { chemicalType: _ct, concentration: _cn, consumption: _cs, ...rest } = values;
    const submitValues: CipSipFormValues = { ...rest, chemical: chemicalData };
    if (existing) {
      onUpdate(existing.id, submitValues);
    } else {
      onAdd(submitValues);
    }
  };

  return (
    <Form {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className="tw-border-border !tw-py-0">
          <CardHeader
            className="tw-bg-secondary/50 tw-p-2 tw-rounded-t-lg tw-cursor-pointer"
            onClick={() => setOpen(v => !v)}
          >
            <div className="tw-flex tw-items-center tw-justify-between">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Droplets className="tw-h-4 tw-w-4 tw-text-primary" />
                <span className="tw-text-sm tw-font-semibold tw-text-foreground">CIP / SIP</span>
              </div>
              {open ? (
                <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
              ) : (
                <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
              )}
            </div>
          </CardHeader>
          {open && (
            <div className={cn(disabled && "tw-opacity-50 tw-pointer-events-none")}>
              <CardContent className="tw-py-3 tw-px-3 tw-space-y-3">
                {/* Frequency */}
                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                  <FormInputNumber
                    control={control}
                    name="frequency.amount"
                    label="Frequency"
                    placeholder="Times"
                  />
                  <FormSelect
                    control={control}
                    name="frequency.per"
                    label="Per"
                    options={INTERVAL_OPTIONS}
                  />
                </div>

                {/* Production */}
                <div className="tw-grid tw-grid-cols-3 tw-gap-2">
                  <FormInputNumber
                    control={control}
                    name="productionAmount.amount"
                    label="Production Amount"
                    placeholder="0"
                  />
                  <FormSelect
                    control={control}
                    name="productionAmount.perUnit"
                    label="Unit"
                    options={PROD_UNIT_OPTIONS}
                  />
                  <FormSelect
                    control={control}
                    name="productionAmount.per"
                    label="Per"
                    options={INTERVAL_OPTIONS}
                  />
                </div>

                {/* Main fields */}
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 tw-gap-2">
                  <FormInput
                    control={control}
                    name="manufacturer"
                    label="Manufacturer"
                    placeholder="Manufacturer"
                  />
                  <FormInput
                    control={control}
                    name="modelNumber"
                    label="Model Number"
                    placeholder="Model"
                  />
                  <FormInputNumber
                    control={control}
                    name="energyConsumption"
                    label="Energy (kWh)"
                    placeholder="0"
                  />
                  <FormInputNumber
                    control={control}
                    name="steamFlow"
                    label="Steam Flow (kg/hr)"
                    placeholder="0"
                  />
                  <FormInputNumber
                    control={control}
                    name="steamPressure"
                    label="Steam Pressure (bar)"
                    placeholder="0"
                  />
                  <FormInputNumber
                    control={control}
                    name="waterConsumption"
                    label="Water (L)"
                    placeholder="0"
                  />
                  <FormInputNumber
                    control={control}
                    name="wasteWater"
                    label="Waste Water (L)"
                    placeholder="0"
                  />
                </div>

                {/* Added Chemicals List */}
                {chemicalData.length > 0 && (
                  <div className="tw-space-y-2">
                    <p className="tw-text-[10px] tw-text-muted-foreground tw-font-medium tw-flex tw-items-center tw-gap-1.5">
                      <FlaskConical className="tw-h-3 tw-w-3" />
                      Added Chemicals ({chemicalData.length})
                    </p>
                    {chemicalData.map((item, index) => (
                      <div
                        key={`chemical_${index}`}
                        className="tw-flex tw-items-center tw-justify-between tw-rounded-md tw-border tw-bg-muted/30 tw-p-1"
                      >
                        <div className="tw-flex-1 tw-grid tw-grid-cols-3 tw-gap-3 tw-text-sm">
                          <div>
                            <span className="tw-text-[10px] tw-text-muted-foreground">
                              Chemical
                            </span>
                            <p className="tw-font-medium tw-text-foreground">{item.chemicalName}</p>
                          </div>
                          <div>
                            <span className="tw-text-[10px] tw-text-muted-foreground">
                              Concentration
                            </span>
                            <p className="tw-font-mono tw-text-foreground">{item.concentration}</p>
                          </div>
                          <div>
                            <span className="tw-text-[10px] tw-text-muted-foreground">
                              Consumption
                            </span>
                            <p className="tw-font-mono tw-text-foreground">{item.consumption}</p>
                          </div>
                        </div>
                        <div className="tw-flex tw-gap-1 tw-shrink-0 tw-ml-3">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="tw-h-7 tw-w-7 tw-text-muted-foreground hover:tw-text-foreground"
                            onClick={() => onEditChemical(item, index)}
                          >
                            <Pencil className="tw-h-3 tw-w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="tw-h-7 tw-w-7 tw-text-destructive"
                            onClick={() => removeChemical(index)}
                          >
                            <Trash2 className="tw-h-3 tw-w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Chemical Form */}
                <div className="tw-border tw-border-dashed tw-border-primary/30 tw-rounded-md tw-p-2 tw-space-y-2 tw-bg-primary/5">
                  <p className="tw-text-[10px] tw-text-muted-foreground tw-font-medium tw-flex tw-items-center tw-gap-1.5">
                    <FlaskConical className="tw-h-3 tw-w-3" />
                    Add Chemical
                  </p>
                  <div className="tw-grid tw-grid-cols-3 tw-gap-2">
                    <FormSelect
                      control={control}
                      name="chemicalType"
                      label="Chemical Name"
                      placeholder="Select chemical"
                      options={(cipSipChemicalTypes ?? []).map((t: any) => ({
                        key: t.key,
                        value: String(t.value),
                        label: t.text
                      }))}
                    />
                    <FormInputNumber
                      control={control}
                      name="concentration"
                      label="Concentration (%)"
                      placeholder="0"
                      rightContentEnable
                      rightContent="%"
                    />
                    <FormInputNumber
                      control={control}
                      name="consumption"
                      label="Consumption"
                      placeholder="0"
                    />
                  </div>
                  <div className="tw-flex tw-justify-end">
                    <Button
                      type="button"
                      size="sm"
                      className="tw-gap-1 tw-text-xs tw-h-7"
                      onClick={addNewChemical}
                      disabled={!chemicalType}
                    >
                      {enableUpdateButton ? (
                        <>
                          <Check className="tw-h-3.5 tw-w-3.5" />
                          Update Chemical
                        </>
                      ) : (
                        <>
                          <Plus className="tw-h-3 tw-w-3" />
                          Add Chemical
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
              <div className="tw-px-3 tw-pb-3">
                <Button
                  type="submit"
                  size="sm"
                  className="tw-h-7 tw-text-xs tw-gap-1 hover:tw-bg-primary/50"
                  disabled={!enableUpdateButton && chemicalData.length === 0}
                >
                  <Save className="tw-h-3 tw-w-3" /> Save
                </Button>
              </div>
            </div>
          )}
        </Card>
      </form>
    </Form>
  );
};

export default CipSipSection;
